/**
 * The account's period of the course, as the Worker keeps it
 * (worker/entitlement.ts, `courseAccess`).
 *
 * Separate from `usePaid`, which asks the only question the catalogue needs —
 * are the paid levels handed out — by asking for them. This one is about the
 * date: when the period ends, and whether the checkout would take a renewal
 * today. It is its own small route rather than a field on `/api/offer`,
 * because the offer is the same answer for every visitor and cached as one.
 */
export interface CourseAccess {
  owned: boolean;
  /** Unix seconds: when the period ends, or ended. Null if there never was one. */
  until: number | null;
  /** The checkout would take another period today. */
  renewable: boolean;
}

/** Null for anybody signed out, and for any answer that is not one. */
export async function fetchAccess(): Promise<CourseAccess | null> {
  let res: Response;
  try {
    res = await fetch('/api/access', { credentials: 'same-origin', cache: 'no-store' });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return null;
  }
  const b = body as Partial<CourseAccess> | null;
  if (!b || typeof b !== 'object' || typeof b.owned !== 'boolean') return null;
  return {
    owned: b.owned,
    until: typeof b.until === 'number' && Number.isFinite(b.until) ? b.until : null,
    renewable: b.renewable === true,
  };
}
